<template>
  <PForm
    ref="pForm"
    :heading="`Associated payments`"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    @close="closeForm"
  >
    <template #header-info>
      <PBadge
        :label="`Vendor : ${paymentRequest?.seller_info?.shop_name}`"
        type="vendor"
        class="q-my-md"
      />
    </template>
    <div class="flex items-center justify-between">
      <div class="q-mb-none text-center">
        <p class="text-weight-medium text-sm q-mb-none">Filter Payments by :</p>
      </div>
      <div class="q-mb-none items-center">
        <PTabs v-model="activeTab" align="left" indicator="chip" dense shrink>
          <PTab
            v-for="({ name, label }, index) in tabs"
            :key="index"
            :class="{ 'text-neutral-70': activeTab !== name }"
            :label="label"
            :name="name"
            class="col"
            indicator="chip"
          />
        </PTabs>
      </div>
    </div>
    <div v-if="loading" class="q-my-md">
      <p>Loading...</p>
    </div>
    <q-card
      v-for="(paymentInfo, index) in pastPayments"
      v-else
      :key="index"
      flat
      :style="{
        minWidth: 'auto',
      }"
      class="relative-position q-mt-xl"
    >
      <q-card-section class="q-pa-none">
        <div class="flex q-mb-xxs items-center justify-between">
          <div>
            <span class="text-weight-bold text-md">
              {{ paymentInfo.internal_number }}
            </span>
          </div>
          <div>
            <div class="text-right">
              <PBadge
                :label="paymentInfo.status_cd"
                :type="paymentInfo.status_cd"
                class="text-capitalize"
              />
            </div>
          </div>
        </div>
        <q-separator />
        <div class="row q-mb-md q-my-sm">
          <div class="col-3">
            <p class="q-mb-xxs text-weight-regular text-sm text-neutral-80">
              Requested at:
            </p>
            <p class="text-weight-regular q-mb-xxs text-sm text-neutral-80">
              Net Payable
            </p>
            <p class="text-weight-regular q-mb-xxs text-sm text-neutral-80">
              Approvers:
            </p>
          </div>
          <div class="col-9">
            <p class="q-mb-xxs text-weight-regular text-sm">
              {{ formatDate(paymentInfo.created_date, "DD MMM, YYYY hh:mm A") }}
            </p>
            <p class="q-mb-xxs text-weight-regular text-sm">
              {{
                numberFormat({
                  number: paymentInfo?.net_payable || 0,
                  style: "currency",
                })
              }}
            </p>
            <p class="q-mb-xxs text-weight-regular text-sm">
              {{ approversInfo }}
            </p>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </PForm>
</template>

<script setup>
import { numberFormat } from "assets/scripts/utilities/helpers";
import { date } from "quasar";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const { formatDate } = date;

const submitButton = ref({});
const secondaryCta = ref({});

const pastPayments = ref([]);
const loading = ref(false);

const { togglePastPaymentRequestsView, getPastPaymentRequests } =
  usePaymentRequestStore();

const { paymentRequest } = storeToRefs(usePaymentRequestStore());

const getStatus = (status_cd) => {
  if (!status_cd) {
    return "-";
  }

  return useChangeCase(status_cd, "capitalCase").value;
};
const approversInfo = computed(() => {
  return unref(paymentRequest)
    ?.approvers?.map(
      (approver) => `${approver.name}: ${getStatus(approver.status_cd)}`,
    )
    .join(", ");
});

const activeTab = ref("all");

const tabs = computed(() => [
  {
    name:
      unref(paymentRequest)?.bill_id?.number ||
      unref(paymentRequest)?.internal_number,
    label:
      unref(paymentRequest)?.bill_number ||
      unref(paymentRequest)?.internal_number,
  },
  {
    name: "all",
    label: "All Vendor bills",
  },
]);

const setPastPaymentRequests = async () => {
  try {
    if (!unref(paymentRequest)) {
      return;
    }

    loading.value = true;

    pastPayments.value = [];

    if (unref(activeTab) !== "all" && unref(paymentRequest).is_advance) {
      pastPayments.value = [unref(paymentRequest)];

      loading.value = false;

      return;
    }

    const options = {};

    const filters = {
      seller: unref(paymentRequest)?.seller?._id,
      skip: unref(paymentRequest)?._id,
      status_cd: [
        "awaiting_approval",
        "approved",
        "initiated",
        "processed",
        "hold",
      ].join(","),
    };

    if (unref(activeTab) !== "all" && unref(paymentRequest)?.bill_id?._id) {
      filters.bill_id = unref(paymentRequest)?.bill_id._id;
    }

    options.params = filters;

    const payments = await getPastPaymentRequests(options);

    pastPayments.value = payments;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

watch([activeTab], () => {
  setPastPaymentRequests();
});

watchEffect(() => {
  setPastPaymentRequests();
});

const closeForm = () => {
  pastPayments.value = [];

  togglePastPaymentRequestsView({
    open: false,
  });
};
</script>
